// Analytics.dataLayer pushes auth-related events to the GTM dataLayer.
// The backend appends `action` and `platform` query params to redirect URLs on
// successful social / corporate authentication; the frontend reads them and
// emits a structured event so GTM tags can attribute the user journey.
//
// Schema mirrors the regular login push in app/Login/loginSagas.js so all auth
// events look the same to GTM, regardless of which flow produced them.
//
// `action` is used verbatim as the `event` name — the backend emits
// `login` / `sign_up`, matching the values GTM expects.
//
// pushAuthEvent returns a Promise that resolves once GTM has finished firing
// every tag triggered by the event (via GTM's `eventCallback`), or after
// `EVENT_TIMEOUT_MS` — whichever comes first. Sagas should `yield call` it
// before navigating away, so the analytics request actually leaves the page
// instead of being killed by the redirect.
//
// The timeout also protects the auth flow when GTM never loads (e.g. an
// ad-blocker dropped it): the promise still resolves and the redirect fires.
//
// Errors are intentionally swallowed: analytics must never block or break an
// auth flow.
import { getSource } from '../helpers';

const EVENT_TIMEOUT_MS = 3000;

export function pushAuthEvent({ action, authMethod, guid, email } = {}) {
  return new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    try {
      if (!action) {
        settle();
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: action,
        // Pass the raw href: decodeURIComponent can throw URIError on malformed
        // percent sequences, and getSource already decodes the captured groups
        // it cares about (with its own try/catch).
        login_source: getSource(window.location.href),
        auth_method: authMethod,
        user_data: {
          user_id: guid,
          logged_in: true,
        },
        // GTM invokes `eventCallback` once every tag triggered by this event
        // has finished, or after `eventTimeout` ms — whichever is sooner.
        eventCallback: settle,
        eventTimeout: EVENT_TIMEOUT_MS,
      });

      if (email) {
        /* eslint-disable no-underscore-dangle */
        window._hsq = window._hsq || [];
        window._hsq.push([
          'identify',
          {
            email,
          },
        ]);
        window._hsq.push(['trackPageView']);
      }

      // Hard ceiling in case GTM never loaded and `eventCallback` will never
      // fire — without this the redirect would hang forever.
      setTimeout(settle, EVENT_TIMEOUT_MS);
    } catch (e) {
      // Analytics failures must not block the auth flow.
      settle();
    }
  });
}
